import React, { Component } from 'react';
import qs from 'query-string';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage } from 'react-intl';
import axios from 'axios';
import compose from 'lodash/fp/compose';
import isEmpty from 'lodash/isEmpty';
import withWidth, { isWidthDown } from '@material-ui/core/withWidth';
import { Typography } from '@material-ui/core';
import { TextField, Button } from '@lavu-pos/lavu-react-components';
import { Link, withRouter } from 'react-router-dom';
import { string as yupString, object as yupObject } from 'yup';
import { Formik } from 'formik';

import { SECTIONS_OBJ } from 'common/constants';
import SwitchCP4Modal from 'common/SwitchCP4Modal';
import ContactInfoModal from 'common/ContactInfoModal';

import {
	lStorage,
	cookiesHelper,
	StyledLink,
	Colors,
	Spacer,
	intlShape,
	notifyToast,
} from 'modules/utils';

import { LOGIN_URL, PHP_URL } from 'utils/constants';

import { withAuthContext } from './context';
import { LEVEL_3_USER, RESTRICTED_USER, INTERNAL_ERROR_MSG } from './constants';
import { LinkWrapper, StyledForm, StyledFormMessage } from './styles';
import { hasDismissedSwitchModal } from './helpers';
import {apolloSubscriptionTransportClient} from "../ApolloSubscriptionTransportClient";

import { loadProtect } from './Protect';

const validationSchema = yupObject().shape({
	username: yupString().required(
		<FormattedMessage id="loginErrors.empty.username" />
	),
	password: yupString().required(
		<FormattedMessage id="loginErrors.empty.password" />
	),
});

class LoginForm extends Component {
	static propTypes = {
		updateUsername: PropTypes.func.isRequired,
		username: PropTypes.string.isRequired,
		history: PropTypes.shape({
			push: PropTypes.func,
		}),
		loginUrl: PropTypes.string,
		title: PropTypes.string,
		width: PropTypes.string,
		isAdmin: PropTypes.bool,
		intl: intlShape,
	};

	static defaultProps = {
		loginUrl: LOGIN_URL,
		title: 'loginTitle',
		isAdmin: false,
	};

	state = {
		errorMessage: null,
		disabledSubmit: true,
		loginSuccess: false,
		accessLevel: false,
		showSwitchCP4Modal: false,
		showContactInfoModal: false,
		isNewCPBusinessContactPopupCancelled: false,
		user: null,
		username: '',
		businessInfo: {
			address: '',
			city: '',
			country: '',
			emailid: '',
			phone: '',
			primary_contact: '',
			state: '',
			zip: '',
		},
	};

	onChangeForm = username => {
		const { updateUsername } = this.props;
		updateUsername(username);
	};

	filterQueryParams = () => {
		// Exclude locationid from query params coming from old CP
		const { locationid, ...params } = qs.parse(
			window.location.search || 'mode=home_home'
		);

		if (this.state.isNewCPBusinessContactPopupCancelled ) {
			params['new_cp_business_contact_popup_cancelled'] = 1;
		}

		return qs.stringify(params);
	};

	navigateToOldCP = () => {
		window.location.href = `${PHP_URL}?${this.filterQueryParams()}`;
	};

	onContactInfoCompleted = () => {
		const { user, username } = this.state;

		this.setState({
			showContactInfoModal: false,
		}, () => this.doLogin(user, username));
	};

	onContactInfoCancelled = () => {
		const { user, username } = this.state;

		this.setState({
			showContactInfoModal: false,
			isNewCPBusinessContactPopupCancelled: true,
		}, () => this.doLogin(user, username));
	};

	onContactInfoError = () => {
		const {
			intl: { formatMessage },
		} = this.props;

		notifyToast({
			kind: 'error',
			message: formatMessage({ id: 'hubqlErrors.UnknownError' }),
		});
	};

	doLogin = (user, username) => {
		const { history } = this.props;

		if (!user.isNewControlPanelActive) {
			if (user.isCP3Beta) {
				const lastDismissedByUser =
					lStorage.get({
						key: 'SWITCH_CP4_MODAL_LAST_DISMISSED',
						stringify: true,
					}) || {};

				if (!hasDismissedSwitchModal(lastDismissedByUser, username)) {
					this.setState({
						showSwitchCP4Modal: true,
					});
				} else {
					this.navigateToOldCP();
				}
			} else {
				this.navigateToOldCP();
			}
		} else {
			const { accessLevel, accessWhiteList, restrictedUser } = user;
			let redirectUrl = '/home';

			if (
				accessLevel === LEVEL_3_USER &&
				restrictedUser === RESTRICTED_USER
			) {
				const accessWhiteListSet = new Set(accessWhiteList.split(','));

				// Depend of the modules available for the user we're going to redirect
				// to the first one in the sidebar
				Object.keys(SECTIONS_OBJ).some(key => {
					if (accessWhiteListSet.has(SECTIONS_OBJ[key].name)) {
						redirectUrl = `/${SECTIONS_OBJ[key].url}`;
						return true;
					}

					return false;
				});
			}

			this.setState(
				{
					errorMessage: null,
					disabledSubmit: true,
				},
				() => {
					history.push(redirectUrl);
					window.scrollTo(0, 0);
				}
			);
		}
	};

	onSubmit = async ({ username, password }, { setSubmitting }) => {
		const { loginUrl, intl, history } = this.props;
		const { accessLevel } = this.state;
		try {
			const { data } = await axios.post(
				loginUrl,
				{
					username,
					password,
					source: 'cp3',
				},
				{ withCredentials: true }
			);

			// If PHPSESSID thorw Internal Error

			if (data.PHPSESSID && data.PHPSESSID !== 'null') {
				const {
					user: {
						languagePack: { googleCode },
					},
					token,
					PHPSESSID,
				} = data;

                //console.log(data.user);

				// set the lavu-token when the login was successful
				cookiesHelper.set('PHPSESSID', PHPSESSID, 30, '/');
				cookiesHelper.set('LAVU_TOKEN', token, 30, '/');
				cookiesHelper.set('CP4_LOCALE', googleCode, 30, '/');
                // Set the cookie for the xsrf token
                //console.log("xsrf:" + data.user.xsrf_token);
                cookiesHelper.set('XSRF-TOKEN', data.user.xsrf_token, 30, '/');
                const userType = data.user.lavuAdmin === 1 ? 'ainf' : 'uinf';
				const userRemoveType = data.user.lavuAdmin === 1 ? 'uinf' : 'ainf';
				cookiesHelper.remove(`${userRemoveType}`);
				cookiesHelper.set(
					`${userType}`,
					data.user.lavu_main_customer_accounts_id,
					30,
					'/'
				);
				lStorage.set({ key: 'current-user', value: { user: data.user } });
				
				// set the enviroment variables after login
				if (!(window?._env_)) {
					window._env_ = {};
				}
				for (const key in data.cp3Env){
					  window._env_[key] = data.cp3Env[key];
				}
			} else {
				const err = new Error(INTERNAL_ERROR_MSG);
				err.response = {
					data: {
						code: 'InternalError',
					},
				};

				throw err;
			}

			const { user } = data;
			const { businessInfo, lavuAdmin } = user;
			const { contact_info_required: contactInfoRequired } = businessInfo;
			if (contactInfoRequired && !lavuAdmin) {
				this.setState({
					businessInfo,
					username,
					user,
					showContactInfoModal: true,
				});
			} else {
				this.doLogin(user, username);
			}

		} catch (error) {
			let message = '';
			let localAccessLevel = accessLevel;
			if (isEmpty(error.response)) {
				message = intl.formatMessage({ id: 'loginErrors.networkErrorMsg' });
			} else if (error.response.headers['x-session']) {
				history.push('/refresh_page?code='+error.response.headers['x-session']);
			} else {
				const { data } = error.response;
				localAccessLevel = data.accessLevelRequired
					? data.accessLevelRequired
					: false;
				message = intl.formatMessage({ id: `hubqlErrors.${data.code}` });
			}

			this.setState({ errorMessage: message, accessLevel: localAccessLevel });
		} finally {
			setSubmitting(false);
		}
	};

	componentWillMount()
	{
		loadProtect();
	}

	render() {

		const {
			errorMessage,
			accessLevel,
			showSwitchCP4Modal,
			showContactInfoModal,
			businessInfo,
			user,
		} = this.state;
		const { title, isAdmin, intl, width } = this.props;

		try {
			if (apolloSubscriptionTransportClient.status === 1) {
				apolloSubscriptionTransportClient.close();
			}
		}
		catch (e) {
			console.error("Error closing Apollo subscription client: ", e);
		}

		return (
			<Formik
				initialValues={{ username: this.props.username || '', password: '' }}
				validationSchema={validationSchema}
				onSubmit={this.onSubmit}
			>
				{({
					values: { username, password },
					errors,
					handleSubmit,
					handleChange,
					handleBlur,
					touched,
					isValid,
					isSubmitting,
				}) => (
					<StyledForm>
						<div>
							<Typography className="login-form-text login-form-title">
								<FormattedMessage id={`commonStrings.${title}`} />
							</Typography>
						</div>
						{isWidthDown('xs', width) && (
							<div>
								<Typography className="login-form-text login-form-welcome-message">
									<FormattedMessage id="commonStrings.mobileWelcomeMessage" />
								</Typography>
							</div>
						)}
						{!errorMessage && <Spacer height={20} />}
						{errorMessage && (
							<StyledFormMessage>
								<Typography
									component="span"
									className="login-form-text login-form-error"
								>
									{errorMessage}
									{!accessLevel && !isAdmin && (
										<>
											{' '}
											<StyledLink
												Component={Link}
												to="/password_reset"
												onClick={() => this.onChangeForm(username)}
												color={Colors.WHITE}
												textDecoration="underline"
											>
												<FormattedMessage id="commonStrings.recoverPassword" />
											</StyledLink>
										</>
									)}
								</Typography>
							</StyledFormMessage>
						)}
						<TextField
							className="login-form-input"
							label={intl.formatMessage({ id: 'commonStrings.username' })}
							type="text"
							name="username"
							value={username}
							autoComplete="username"
							error={touched.username && errors.username}
							onChange={handleChange}
							onBlur={handleBlur}
							initialFocus
						/>
						<Spacer height={20} />
						<TextField
							className="login-form-input"
							label={intl.formatMessage({ id: 'commonStrings.password' })}
							type="password"
							name="password"
							value={password}
							error={touched.password && errors.password}
							autoComplete="new-password"
							onChange={handleChange}
							onBlur={handleBlur}
						/>
						<Spacer height={5} />
						{!isAdmin && (
							<LinkWrapper>
								<StyledLink
									className="login-form-password-reset-link"
									Component={Link}
									to="/password_reset"
									onClick={() => this.onChangeForm(username)}
								>
									<Typography className="login-form-text login-form-forgot-password">
										<FormattedMessage id="commonStrings.forgotPassword" />
									</Typography>
								</StyledLink>
							</LinkWrapper>
						)}
						<Spacer height={20} />
						<Button
							disabled={!isValid || isSubmitting}
							type="submit"
							kind="success"
							fluid
							onClick={handleSubmit}
						>
							<FormattedMessage id="commonStrings.signIn" />
						</Button>
						<SwitchCP4Modal
							open={showSwitchCP4Modal}
							username={username}
							onClose={() => this.setState({ showSwitchCP4Modal: false })}
							navigateToOldCP={this.navigateToOldCP}
						/>
						<ContactInfoModal
							open={showContactInfoModal}
							businessInfo={businessInfo}
							user={user}
							onClose={this.onContactInfoCompleted}
							onCompleted={this.onContactInfoCompleted}
							onCancel={this.onContactInfoCancelled}
							onError={this.onContactInfoError}
						/>
					</StyledForm>
				)}
			</Formik>
		);
	}
}

export default compose(
	injectIntl,
	withWidth(),
	withAuthContext(({ updateUsername, username }) => ({
		updateUsername,
		username,
	})),
	withRouter
)(LoginForm);
